/**
 * This file contains code related to date ranges and static ranges.
 * It imports functions from the 'date-fns' and 'react-date-range' libraries.
 * The code defines various date ranges and static ranges for use in date selection.
 * It also exports functions to create static ranges and defines default static ranges.
 * Additionally, it defines input ranges and their corresponding label, range, and current value functions.
 */

import {
    addDays,
    addMonths,
    differenceInCalendarDays,
    endOfDay,
    endOfMonth,
    endOfWeek,
    isSameDay,
    startOfDay,
    startOfMonth,
    startOfWeek
} from "date-fns";
import {DefinedRangeProps, Preview, Range, StaticRange} from "react-date-range";

// date-fns dates based on current date. To be used in static ranges
const defineds = {
    startOfWeek: startOfWeek(new Date()),
    endOfWeek: endOfWeek(new Date()),
    startOfLastWeek: startOfWeek(addDays(new Date(), -7)),
    endOfLastWeek: endOfWeek(addDays(new Date(), -7)),
    startOfToday: startOfDay(new Date()),
    endOfToday: endOfDay(new Date()),
    startOfYesterday: startOfDay(addDays(new Date(), -1)),
    endOfYesterday: endOfDay(addDays(new Date(), -1)),
    startOfMonth: startOfMonth(new Date()),
    endOfMonth: endOfMonth(new Date()),
    startOfLastMonth: startOfMonth(addMonths(new Date(), -1)),
    endOfLastMonth: endOfMonth(addMonths(new Date(), -1)),
};

// Helper function to use in mapping of StaticRanges, provides default values and isSelected logic
const staticRangeHandler = {
    range: function () {
        return {
            startDate: new Date(),
            endDate: new Date(),
            color: undefined, // color is optional
        };
    },
    isSelected: function (range: Range) {
        const definedRange = this.range();

        return (
            isSameDay(range.startDate || 0, definedRange.startDate) &&
            isSameDay(range.endDate || 0, definedRange.endDate)
        );
    },
};

export interface BaseStaticRange {
    label: string,
    range: () => Preview,
}

export function createStaticRanges(ranges: BaseStaticRange[]): StaticRange[] {
    return ranges.map(range => ({...staticRangeHandler, ...range}));
}

export const defaultStaticRanges = createStaticRanges([
        {
            label: 'Hoy',
            range: (): Preview => ({
                startDate: defineds.startOfToday,
                endDate: defineds.endOfToday,
                color: undefined,
            }),
        },

        {
            label: 'Ayer',
            range: () => ({
                startDate: defineds.startOfYesterday,
                endDate: defineds.endOfYesterday,
                color: undefined,
            }),
        },

        {
            label: 'Esta semana',
            range: () => ({
                startDate: defineds.startOfWeek,
                endDate: defineds.endOfWeek,
                color: undefined,
            }),
        },
        {
            label: 'Semana pasada',
            range: () => ({
                startDate: defineds.startOfLastWeek,
                endDate: defineds.endOfLastWeek,
                color: undefined,
            }),
        },
        {
            label: 'Este Mes',
            range: () => ({
                startDate: defineds.startOfMonth,
                endDate: defineds.endOfMonth,
                color: undefined,
            }),
        },
        {
            label: 'Mes Pasado',
            range: () => ({
                startDate: defineds.startOfLastMonth,
                endDate: defineds.endOfLastMonth,
                color: undefined,
            }),
        },
    ]
);

export interface InputRange {
    range: (value: number, props?: DefinedRangeProps) => Range;
    getCurrentValue: (range: Range) => string | number;
    label?: string | undefined;
}

export const defaultInputRanges: InputRange[] = [
    {
        label: 'días hasta hoy',
        range(value: number) {
            return {
                startDate: addDays(defineds.startOfToday, (Math.max(Number(value), 1) - 1) * -1),
                endDate: defineds.endOfToday,
            };
        },
        getCurrentValue(range: Range) {
            if (!isSameDay(range.endDate || 0, defineds.endOfToday)) return '-';
            if (!range.startDate) return '∞';

            return differenceInCalendarDays(defineds.endOfToday, range.startDate) + 1;
        },
    },
    {
        label: 'días desde hoy',
        range(value: number) {
            const today = new Date();

            return {
                startDate: today,
                endDate: addDays(today, Math.max(Number(value), 1) - 1),
            };
        },
        getCurrentValue(range: Range) {
            if (!isSameDay(range.startDate || 0, defineds.startOfToday)) return '-';
            if (!range.endDate) return '∞';

            return differenceInCalendarDays(range.endDate, defineds.startOfToday) + 1;
        },
    },
];